import { FC, ReactNode } from 'react'

import styled from 'styled-components'

import { PrimaryLabel } from 'src/Components/PrimaryLabel'
import { PreReleaseLabel } from 'src/Components/PreReleaseLabel'
import SpotifyIcon from 'src/Assets/Svg/platform-icons/Spotify.svg?react'
import TikTokIcon from 'src/Assets/Svg/platform-icons/TikTok.svg?react'
import ReelsIcon from 'src/Assets/Svg/platform-icons/Reels.svg?react'
import MetaAdsIcon from 'src/Assets/Svg/platform-icons/meta-ads.svg?react'
import {
  CampaignPlatformTypeEnum,
  SpotifyCampaignMatchingType,
} from 'src/Types/campaigns'

import { ScheduleSummaryProps } from '..'
import { ManualMatchingLabel } from '../../ManualMatchingLabel'

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`

const Cover = styled.img`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 12px;
  object-fit: cover;
  background: ${({ theme }) => theme.color.hoverWhite};
`

const CoverPlaceholder = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 12px;
  background: ${({ theme }) => theme.color.hoverWhite};
`

const TrackInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
  min-width: 0;
`

const TrackInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const TrackName = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`

const ArtistName = styled(TrackName)`
  font-weight: 400;
`

const LabelsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
`

const platformIconMap: Partial<Record<CampaignPlatformTypeEnum, ReactNode>> = {
  [CampaignPlatformTypeEnum.SPOTIFY]: <SpotifyIcon />,
  [CampaignPlatformTypeEnum.TIKTOK]: <TikTokIcon />,
  [CampaignPlatformTypeEnum.REELS]: <ReelsIcon />,
  [CampaignPlatformTypeEnum.META]: <MetaAdsIcon width={16} height={16} />,
}

const platformTextMap: Partial<Record<CampaignPlatformTypeEnum, ReactNode>> = {
  [CampaignPlatformTypeEnum.SPOTIFY]: 'Spotify',
  [CampaignPlatformTypeEnum.TIKTOK]: 'TikTok',
  [CampaignPlatformTypeEnum.REELS]: 'Reels',
  [CampaignPlatformTypeEnum.META]: 'Meta Ads',
}

type Props = Pick<
  ScheduleSummaryProps,
  | 'artistName'
  | 'trackName'
  | 'platform'
  | 'matchingType'
  | 'trackCover'
  | 'isPreRelease'
>

export const ScheduleSummaryTrackDetails: FC<Props> = ({
  trackName,
  artistName,
  platform,
  matchingType,
  trackCover,
  isPreRelease,
}) => (
  <Container>
    {trackCover ? (
      <Cover src={trackCover} alt={trackName} />
    ) : (
      <CoverPlaceholder />
    )}
    <TrackInfoContainer>
      <TrackInfo>
        <TrackName>{trackName}</TrackName>
        <ArtistName>{artistName}</ArtistName>
      </TrackInfo>
      <LabelsWrapper>
        <PrimaryLabel text={platformTextMap[platform]}>
          {platformIconMap[platform]}
        </PrimaryLabel>
        {Boolean(isPreRelease) &&
          platform === CampaignPlatformTypeEnum.SPOTIFY && <PreReleaseLabel />}
        {matchingType === SpotifyCampaignMatchingType.MANUAL && (
          <ManualMatchingLabel />
        )}
      </LabelsWrapper>
    </TrackInfoContainer>
  </Container>
)
